import { z } from 'zod';

import { ApiError, apiErrorMessage } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import type { SyncSearch } from './sync-search';
import {
  ODOO_OPENING_BALANCE_ERROR_CODES,
  OPENING_BALANCE_EXCLUSIONS,
  type OpeningBalanceExcluded,
  type OpeningBalanceExclusion,
  type OpeningBalanceFilters,
  type OpeningBalancePostResult,
  type OpeningBalancePreview,
} from './types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const OPENING_BALANCE_PAGE_SIZE = 25;
export const OPENING_BALANCE_EVENT_TYPE = 'contract.opening_balance';

export const OpeningBalanceSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type OpeningBalanceSearch = z.infer<typeof OpeningBalanceSearchSchema>;

export function openingBalanceFiltersFromSearch(
  search: OpeningBalanceSearch
): OpeningBalanceFilters {
  const term = search.search?.trim();
  return {
    search: term ? term : undefined,
    page: search.page ?? 1,
    pageSize: OPENING_BALANCE_PAGE_SIZE,
  };
}

/**
 * Totals and the post count come from the unfiltered first page, so a search
 * never changes what "Post" would send. Same key as the list's page 1, so the
 * common case is one request.
 */
export const OPENING_BALANCE_SUMMARY_FILTERS: OpeningBalanceFilters = {
  search: undefined,
  page: 1,
  pageSize: OPENING_BALANCE_PAGE_SIZE,
};

/** Sync Health scoped to the opening invoices just queued. */
export const openingBalanceSyncSearch = (): Pick<SyncSearch, 'status' | 'eventType'> => ({
  status: 'all',
  eventType: OPENING_BALANCE_EVENT_TYPE,
});

export type ExclusionView = Readonly<{
  reason: OpeningBalanceExclusion;
  count: number;
  labelKey: string;
  explanationKey: string;
}>;

const EXCLUSION_LABEL_KEYS: Record<OpeningBalanceExclusion, string> = {
  zero_balance: 'odoo.openingBalances.excluded.zero_balance.label',
  written_off: 'odoo.openingBalances.excluded.written_off.label',
  already_in_odoo: 'odoo.openingBalances.excluded.already_in_odoo.label',
  no_loan: 'odoo.openingBalances.excluded.no_loan.label',
  loan_unknown: 'odoo.openingBalances.excluded.loan_unknown.label',
  not_eligible_status: 'odoo.openingBalances.excluded.not_eligible_status.label',
};

const EXCLUSION_EXPLANATION_KEYS: Record<OpeningBalanceExclusion, string> = {
  zero_balance: 'odoo.openingBalances.excluded.zero_balance.explanation',
  written_off: 'odoo.openingBalances.excluded.written_off.explanation',
  already_in_odoo: 'odoo.openingBalances.excluded.already_in_odoo.explanation',
  no_loan: 'odoo.openingBalances.excluded.no_loan.explanation',
  loan_unknown: 'odoo.openingBalances.excluded.loan_unknown.explanation',
  not_eligible_status: 'odoo.openingBalances.excluded.not_eligible_status.explanation',
};

export function describeExclusion(
  reason: OpeningBalanceExclusion,
  count: number
): ExclusionView {
  return {
    reason,
    count,
    labelKey: EXCLUSION_LABEL_KEYS[reason],
    explanationKey: EXCLUSION_EXPLANATION_KEYS[reason],
  };
}

export const excludedTotal = (excluded: OpeningBalanceExcluded | undefined): number =>
  excluded ? OPENING_BALANCE_EXCLUSIONS.reduce((sum, r) => sum + excluded[r], 0) : 0;

/** Reasons that excluded at least one contract, largest first; ties keep the declared order. */
export function exclusionBreakdown(
  excluded: OpeningBalanceExcluded | undefined
): ExclusionView[] {
  if (!excluded) return [];
  return OPENING_BALANCE_EXCLUSIONS.map((r) => describeExclusion(r, excluded[r]))
    .filter((v) => v.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** The amount tiles and toasts show; opening balances settle in USD. */
export const formatOpeningAmount = (amount: number): string =>
  formatCurrency(amount, 'USD');

export type OpeningTotalsView = Readonly<{
  contracts: number;
  amount: string;
  excluded: number;
}>;

export function openingTotalsView(
  preview: Pick<OpeningBalancePreview, 'totals' | 'excluded'>
): OpeningTotalsView {
  return {
    contracts: preview.totals.contracts,
    amount: formatOpeningAmount(preview.totals.amount),
    excluded: excludedTotal(preview.excluded),
  };
}

export const postResultParams = (result: OpeningBalancePostResult) => ({
  enqueued: result.enqueued,
  skipped: result.skipped,
  amount: formatOpeningAmount(result.totalAmount),
});

/** Exact digits after trimming; "1,204" and "1204 " are not the same as 1204 and 1204. */
export function confirmCountMatches(typed: string, count: number): boolean {
  return count > 0 && typed.trim() === String(count);
}

const OpeningBalanceConfirmSchema = z.object({
  confirmCount: z.string().trim(),
});

export const createOpeningBalanceConfirmSchema = (t: Translate, count: number) =>
  OpeningBalanceConfirmSchema.extend({
    confirmCount: z
      .string()
      .trim()
      .refine(
        (typed) => confirmCountMatches(typed, count),
        t('odoo.openingBalances.post.validation.countMismatch', { count })
      )
      .transform(() => count),
  });

export type PostButtonInput = Readonly<{
  canUpdate: boolean;
  /** Null while the preview is loading or failed. */
  count: number | null;
  /** Null while settings are loading. */
  accountSet: boolean | null;
  pending: boolean;
}>;

export type PostButtonState = Readonly<{
  visible: boolean;
  enabled: boolean;
  /** Why it is disabled, for the tooltip and screen readers. */
  reasonKey: string | null;
}>;

export function postButtonState(input: PostButtonInput): PostButtonState {
  if (!input.canUpdate) return { visible: false, enabled: false, reasonKey: null };
  const disabled = (reasonKey: string | null): PostButtonState => ({
    visible: true,
    enabled: false,
    reasonKey,
  });
  if (input.pending) return disabled(null);
  if (input.count === null || input.accountSet === null) return disabled(null);
  if (input.accountSet === false) {
    return disabled('odoo.openingBalances.post.disabled.accountNotSet');
  }
  if (input.count === 0) return disabled('odoo.openingBalances.post.disabled.nothingToPost');
  return { visible: true, enabled: true, reasonKey: null };
}

export type OpeningBalanceErrorAction = 'goLive' | 'settings' | 'account' | 'retry' | null;

export type OpeningBalanceErrorView = Readonly<{
  titleKey: string;
  /** Null: show the server's own text. */
  descriptionKey: string | null;
  action: OpeningBalanceErrorAction;
  /** Setup the operator still has to do, rather than a failure. */
  tone: 'info' | 'warning' | 'destructive';
  /** The preview is out of date and should be fetched again. */
  refreshPreview: boolean;
}>;

const ERROR_VIEWS: Readonly<Record<string, OpeningBalanceErrorView>> = {
  [ODOO_OPENING_BALANCE_ERROR_CODES.goLiveNotSet]: {
    titleKey: 'odoo.openingBalances.errors.goLiveNotSet.title',
    descriptionKey: 'odoo.openingBalances.errors.goLiveNotSet.body',
    action: 'goLive',
    tone: 'info',
    refreshPreview: false,
  },
  [ODOO_OPENING_BALANCE_ERROR_CODES.notConfigured]: {
    titleKey: 'odoo.openingBalances.errors.notConfigured.title',
    descriptionKey: 'odoo.openingBalances.errors.notConfigured.body',
    action: 'settings',
    tone: 'info',
    refreshPreview: false,
  },
  [ODOO_OPENING_BALANCE_ERROR_CODES.loanServiceUnavailable]: {
    titleKey: 'odoo.openingBalances.errors.loanServiceUnavailable.title',
    descriptionKey: 'odoo.openingBalances.errors.loanServiceUnavailable.body',
    action: 'retry',
    tone: 'warning',
    refreshPreview: false,
  },
  [ODOO_OPENING_BALANCE_ERROR_CODES.countMismatch]: {
    titleKey: 'odoo.openingBalances.errors.countMismatch.title',
    descriptionKey: 'odoo.openingBalances.errors.countMismatch.body',
    action: null,
    tone: 'warning',
    refreshPreview: true,
  },
  [ODOO_OPENING_BALANCE_ERROR_CODES.accountNotSet]: {
    titleKey: 'odoo.openingBalances.errors.accountNotSet.title',
    descriptionKey: 'odoo.openingBalances.errors.accountNotSet.body',
    action: 'account',
    tone: 'warning',
    refreshPreview: false,
  },
};

const GENERIC_ERROR: OpeningBalanceErrorView = {
  titleKey: 'odoo.openingBalances.errors.generic.title',
  descriptionKey: null,
  action: 'retry',
  tone: 'destructive',
  refreshPreview: false,
};

export function openingBalanceErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.errorCode) return error.errorCode;
  return error.statusCode === 503
    ? ODOO_OPENING_BALANCE_ERROR_CODES.loanServiceUnavailable
    : null;
}

export const describeOpeningBalanceError = (error: unknown): OpeningBalanceErrorView =>
  ERROR_VIEWS[openingBalanceErrorCode(error) ?? ''] ?? GENERIC_ERROR;

/** One line for the dialog's live region. */
export function openingBalanceErrorMessage(t: Translate, error: unknown): string | null {
  if (!error) return null;
  const view = describeOpeningBalanceError(error);
  return view.descriptionKey
    ? `${t(view.titleKey)} ${t(view.descriptionKey)}`
    : apiErrorMessage(error, t(view.titleKey));
}
